import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Select,
  DatePicker,
  Space,
  Progress,
  Tabs
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  LineChartOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { Line, Pie } from '@ant-design/charts';
import { useAccuracyMetrics, useCostTrends, useVarianceAnalysis } from '../../hooks/useEstimation';
import { useCurrency } from '@/contexts/CurrencyContext';
import { AedDirhamSign } from '@/components/currency';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

export const HistoricalAnalysisPage: React.FC = () => {
  const { formatCurrency, baseCurrency } = useCurrency();
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedEstimationId, setSelectedEstimationId] = useState<string>('');
  const [periodMonths, setPeriodMonths] = useState<number>(12);

  // Queries
  const { data: accuracyData, isLoading: accuracyLoading } = useAccuracyMetrics();
  const { data: trendsData, isLoading: trendsLoading } = useCostTrends(selectedItemId, periodMonths);
  const { data: varianceData, isLoading: varianceLoading } = useVarianceAnalysis(selectedEstimationId);

  const accuracyMetrics = ((accuracyData as { data?: Record<string, unknown> })?.data ??
    (accuracyData as Record<string, unknown>) ??
    {}) as Record<string, any>;
  const costTrends = Array.isArray(trendsData)
    ? trendsData
    : (trendsData as { data?: unknown[] })?.data ?? [];
  const varianceAnalysis = ((varianceData as { data?: unknown })?.data ??
    varianceData ??
    null) as Record<string, any> | null;

  // Variance table columns
  const varianceColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category'
    },
    {
      title: 'Estimated',
      dataIndex: 'estimatedAmount',
      key: 'estimatedAmount',
      render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED')
    },
    {
      title: 'Actual',
      dataIndex: 'actualAmount',
      key: 'actualAmount',
      render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED')
    },
    {
      title: 'Variance',
      dataIndex: 'variance',
      key: 'variance',
      render: (value: number, record: any) => (
        <Space>
          <span style={{ color: record.isFavorable ? '#52c41a' : '#ff4d4f' }}>
            {value > 0 ? '+' : ''}
            {value.toLocaleString()}
          </span>
          <Tag color={record.isFavorable ? 'success' : 'error'} icon={value < 0 ? <ArrowDownOutlined /> : <ArrowUpOutlined />}>
            {record.variancePercentage.toFixed(1)}%
          </Tag>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isFavorable',
      key: 'isFavorable',
      render: (isFavorable: boolean) => (
        <Tag color={isFavorable ? 'success' : 'error'} icon={isFavorable ? <CheckCircleOutlined /> : <WarningOutlined />}>
          {isFavorable ? 'Favorable' : 'Unfavorable'}
        </Tag>
      )
    }
  ];

  // Root cause columns
  const rootCauseColumns = [
    {
      title: 'Root Cause',
      dataIndex: 'cause',
      key: 'cause'
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (cat: string) => <Tag>{cat}</Tag>
    },
    {
      title: 'Impact',
      dataIndex: 'impact',
      key: 'impact',
      render: (value: number) => formatCurrency(value, baseCurrency?.currency_code || 'AED'),
      sorter: (a: any, b: any) => b.impact - a.impact
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (value: number) => <Tag color="blue">{value} times</Tag>
    }
  ];

  // Prepare cost trend chart data
  const costTrendChartData = costTrends.map((trend: any) => ({
    date: new Date(trend.date).toLocaleDateString(),
    cost: trend.unitCost,
    movingAverage: trend.movingAverage,
    type: trend.isAnomaly ? 'Anomaly' : 'Normal'
  }));

  // Cost trend chart config
  const costTrendConfig = {
    data: costTrendChartData,
    xField: 'date',
    yField: 'cost',
    seriesField: 'type',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000
      }
    }
  };

  // Variance pie chart data (if variance analysis exists)
  const variancePieData = varianceAnalysis
    ? [
        { category: 'Material', value: Math.abs(varianceAnalysis.estimatedCosts.materialCost - varianceAnalysis.actualCosts.materialCost) },
        { category: 'Labor', value: Math.abs(varianceAnalysis.estimatedCosts.laborCost - varianceAnalysis.actualCosts.laborCost) },
        { category: 'Overhead', value: Math.abs(varianceAnalysis.estimatedCosts.overheadCost - varianceAnalysis.actualCosts.overheadCost) },
        { category: 'Tooling', value: Math.abs(varianceAnalysis.estimatedCosts.toolingCost - varianceAnalysis.actualCosts.toolingCost) }
      ].filter(d => d.value > 0)
    : [];

  const variancePieConfig = {
    data: variancePieData,
    angleField: 'value',
    colorField: 'category',
    radius: 0.8,
    label: {
      type: 'outer',
      content: '{name} {percentage}'
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 8 }}>
          <BarChartOutlined style={{ marginRight: 8 }} />
          Historical Cost Analysis
        </h1>
        <p style={{ color: '#8c8c8c' }}>
          Track estimation accuracy, variance analysis, and cost trends over time
        </p>
      </div>

      {/* Accuracy Summary */}
      <Card title="Overall Estimation Accuracy" loading={accuracyLoading} style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic
              title="Total Estimations"
              value={accuracyMetrics.totalEstimations || 0}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Completed"
              value={accuracyMetrics.completedEstimations || 0}
              valueStyle={{ color: '#145A54' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Accuracy Rate"
              value={accuracyMetrics.accuracyRate || 0}
              suffix="%"
              valueStyle={{ color: '#52c41a' }}
            />
            <Progress percent={accuracyMetrics.accuracyRate || 0} showInfo={false} status="success" />
          </Col>
          <Col span={6}>
            <Statistic
              title="Avg Variance"
              value={accuracyMetrics.averageVariancePercentage || 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 24 }}>
          <Col span={12}>
            <Card size="small" title="Estimation Bias">
              <Space size="large">
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Overestimation</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: '#52c41a' }}>
                    {accuracyMetrics.overestimationPercentage?.toFixed(1) || 0}%
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                    ({accuracyMetrics.overestimationCount || 0} cases)
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Underestimation</div>
                  <div style={{ fontSize: 20, fontWeight: 600, color: '#ff4d4f' }}>
                    {accuracyMetrics.underestimationPercentage?.toFixed(1) || 0}%
                  </div>
                  <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                    ({accuracyMetrics.underestimationCount || 0} cases)
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" title="Performance Trend">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Last 30 Days"
                    value={accuracyMetrics.trend?.last30Days || 0}
                    suffix="%"
                    precision={0}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Last 90 Days"
                    value={accuracyMetrics.trend?.last90Days || 0}
                    suffix="%"
                    precision={0}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Improvement"
                    value={accuracyMetrics.trend?.improvementRate || 0}
                    suffix="%"
                    prefix={accuracyMetrics.trend?.isImproving ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    valueStyle={{
                      fontSize: 16,
                      color: accuracyMetrics.trend?.isImproving ? '#52c41a' : '#ff4d4f'
                    }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* Tabs for different analyses */}
      <Tabs defaultActiveKey="variance">
        <TabPane tab="Variance Analysis" key="variance" icon={<BarChartOutlined />}>
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Select
                  style={{ width: 300 }}
                  placeholder="Select estimation to analyze"
                  value={selectedEstimationId}
                  onChange={setSelectedEstimationId}
                >
                  <Option value="est-1">EST-2025-00001 - Sample Estimation</Option>
                  <Option value="est-2">EST-2025-00002 - Another Estimation</Option>
                </Select>
              </Space>
            </div>

            {varianceAnalysis ? (
              <>
                {/* Variance Summary */}
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={12}>
                    <Card size="small" title="Cost Breakdown">
                      <Table
                        columns={varianceColumns}
                        dataSource={varianceAnalysis.varianceCategories}
                        rowKey="category"
                        pagination={false}
                        size="small"
                      />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card size="small" title="Variance Distribution">
                      <Pie {...variancePieConfig} />
                    </Card>
                  </Col>
                </Row>

                {/* Root Causes */}
                {varianceAnalysis.rootCauses && varianceAnalysis.rootCauses.length > 0 && (
                  <Card size="small" title="Root Cause Analysis" style={{ marginBottom: 16 }}>
                    <Table
                      columns={rootCauseColumns}
                      dataSource={varianceAnalysis.rootCauses}
                      rowKey="cause"
                      pagination={false}
                      size="small"
                    />
                  </Card>
                )}

                {/* Recommendation */}
                <Card
                  size="small"
                  style={{ background: '#e6f7ff', border: '1px solid #91d5ff' }}
                >
                  <strong>Recommendation:</strong> {varianceAnalysis.recommendation}
                </Card>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 48, color: '#8c8c8c' }}>
                Select an estimation to view variance analysis
              </div>
            )}
          </Card>
        </TabPane>

        <TabPane tab="Cost Trends" key="trends" icon={<LineChartOutlined />}>
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Select
                  style={{ width: 300 }}
                  placeholder="Select item to analyze"
                  value={selectedItemId}
                  onChange={setSelectedItemId}
                >
                  <Option value="item-1">Item-001 - Steel Bracket</Option>
                  <Option value="item-2">Item-002 - Aluminum Housing</Option>
                </Select>
                <Select
                  style={{ width: 150 }}
                  value={periodMonths}
                  onChange={setPeriodMonths}
                >
                  <Option value={3}>Last 3 Months</Option>
                  <Option value={6}>Last 6 Months</Option>
                  <Option value={12}>Last 12 Months</Option>
                  <Option value={24}>Last 24 Months</Option>
                </Select>
              </Space>
            </div>

            {costTrends.length > 0 ? (
              <>
                <Line {...costTrendConfig} />

                <Card size="small" title="Trend Summary" style={{ marginTop: 16 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="Latest Cost"
                        value={costTrends[costTrends.length - 1]?.unitCost || 0}
                        prefix={<AedDirhamSign />}
                        precision={2}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Average Cost"
                        value={
                          costTrends.reduce((sum: number, t: any) => sum + t.unitCost, 0) /
                            costTrends.length || 0
                        }
                        prefix={<AedDirhamSign />}
                        precision={2}
                      />
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="Anomalies Detected"
                        value={costTrends.filter((t: any) => t.isAnomaly).length}
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Col>
                  </Row>
                </Card>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 48, color: '#8c8c8c' }}>
                Select an item to view cost trends
              </div>
            )}
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default HistoricalAnalysisPage;

